import { z } from 'zod';
/** Schrift-Allowlist aus Anhang B. Andere Schriften sind nicht erlaubt. */
export const FONT_IDS = [
    'playwrite-de-va',
    'playwrite-de-la',
    'playwrite-de-sas',
    'playwrite-de-grund',
    'playwrite-at',
    'grundschrift',
    'gruschudru-basic',
    'fibel-nord',
    'andika',
    'atkinson',
    'abeezee',
    'opendyslexic',
];
export const fontIdSchema = z.enum(FONT_IDS, { error: 'Unbekannte Schrift-ID' });
export const fontRefSchema = z.object({
    family: fontIdSchema,
    weight: z.union([z.literal(400), z.literal(700)]).optional(),
    italic: z.boolean().optional(),
});
export const DEFAULT_FONT = { family: 'playwrite-de-grund' };
export const DEFAULT_UI_FONT = { family: 'andika' };
const playwrite = (name, file, purpose, cssFamily, upstream, extra = []) => ({
    name,
    purpose,
    cssFamily,
    files: [{ style: 'variable', woff2: `${file}.woff2`, print: `${file}.ttf` }, ...extra],
    license: {
        spdx: 'OFL-1.1',
        author: 'TypeTogether',
        sourceUrl: upstream,
        file: 'OFL.txt',
        attributionRequired: false,
    },
    status: 'available',
});
export const FONTS = {
    'playwrite-de-va': playwrite('Playwrite DE VA', 'PlaywriteDEVA[wght]', 'ausgangsschrift', 'Playwrite DE VA', 'https://github.com/google/fonts/tree/main/ofl/playwritedeva', [
        {
            style: 'guides',
            woff2: 'PlaywriteDEVAGuides-Regular.woff2',
            print: 'PlaywriteDEVAGuides-Regular.ttf',
        },
    ]),
    'playwrite-de-la': playwrite('Playwrite DE LA', 'PlaywriteDELA[wght]', 'ausgangsschrift', 'Playwrite DE LA', 'https://github.com/google/fonts/tree/main/ofl/playwritedela'),
    'playwrite-de-sas': playwrite('Playwrite DE SAS', 'PlaywriteDESAS[wght]', 'ausgangsschrift', 'Playwrite DE SAS', 'https://github.com/google/fonts/tree/main/ofl/playwritedesas'),
    'playwrite-de-grund': playwrite('Playwrite DE Grund', 'PlaywriteDEGrund[wght]', 'ausgangsschrift', 'Playwrite DE Grund', 'https://github.com/google/fonts/tree/main/ofl/playwritedegrund'),
    'playwrite-at': playwrite('Playwrite AT', 'PlaywriteAT[wght]', 'ausgangsschrift', 'Playwrite AT', 'https://github.com/google/fonts/tree/main/ofl/playwriteat', [
        {
            style: 'italic',
            woff2: 'PlaywriteAT-Italic[wght].woff2',
            print: 'PlaywriteAT-Italic[wght].ttf',
            italic: true,
        },
    ]),
    grundschrift: {
        name: 'Grundschrift',
        purpose: 'druckschrift',
        cssFamily: 'Grundschrift',
        files: [
            {
                style: 'regular',
                woff2: 'Grundschrift-Regular.woff2',
                print: 'Grundschrift-Regular.otf',
                weight: 400,
            },
        ],
        license: {
            spdx: 'CC-BY-3.0',
            author: 'Christian Urff',
            sourceUrl: 'https://urff.app/grundschrift/',
            file: 'LICENSE.txt',
            attributionRequired: true,
            attributionText: 'Grundschrift von Christian Urff, CC BY 3.0',
        },
        status: 'available',
    },
    'gruschudru-basic': {
        name: 'Gruschudru Basic',
        purpose: 'druckschrift',
        cssFamily: 'Gruschudru Basic',
        files: [
            {
                style: 'regular',
                woff2: 'GruschudruBasic-Regular.woff2',
                print: 'GruschudruBasic-Regular.ttf',
                weight: 400,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'Florian Emrich, Annie Olsen',
            sourceUrl: 'vom Auftraggeber bereitgestellt',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'pending',
        pendingReason: 'Schriftdatei und OFL.txt mit Copyright beider Urheber stellt der Auftraggeber bereit (Anhang B/D).',
    },
    'fibel-nord': {
        name: 'Fibel Nord',
        purpose: 'druckschrift',
        cssFamily: 'Fibel Nord',
        files: [
            {
                style: 'regular',
                woff2: 'FibelNord-Regular.woff2',
                print: 'FibelNord-Regular.ttf',
                weight: 400,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'Peter Wiegel',
            sourceUrl: 'https://www.peter-wiegel.de/Fibel.html',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'available',
    },
    andika: {
        name: 'Andika',
        purpose: 'leselernschrift',
        cssFamily: 'Andika',
        files: [
            { style: 'regular', woff2: 'Andika-Regular.woff2', print: 'Andika-Regular.ttf', weight: 400 },
            { style: 'bold', woff2: 'Andika-Bold.woff2', print: 'Andika-Bold.ttf', weight: 700 },
            {
                style: 'italic',
                woff2: 'Andika-Italic.woff2',
                print: 'Andika-Italic.ttf',
                weight: 400,
                italic: true,
            },
            {
                style: 'boldItalic',
                woff2: 'Andika-BoldItalic.woff2',
                print: 'Andika-BoldItalic.ttf',
                weight: 700,
                italic: true,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'SIL International',
            sourceUrl: 'https://github.com/google/fonts/tree/main/ofl/andika',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'available',
    },
    atkinson: {
        name: 'Atkinson Hyperlegible',
        purpose: 'barrierefrei',
        cssFamily: 'Atkinson Hyperlegible',
        files: [
            {
                style: 'regular',
                woff2: 'AtkinsonHyperlegible-Regular.woff2',
                print: 'AtkinsonHyperlegible-Regular.ttf',
                weight: 400,
            },
            {
                style: 'bold',
                woff2: 'AtkinsonHyperlegible-Bold.woff2',
                print: 'AtkinsonHyperlegible-Bold.ttf',
                weight: 700,
            },
            {
                style: 'italic',
                woff2: 'AtkinsonHyperlegible-Italic.woff2',
                print: 'AtkinsonHyperlegible-Italic.ttf',
                weight: 400,
                italic: true,
            },
            {
                style: 'boldItalic',
                woff2: 'AtkinsonHyperlegible-BoldItalic.woff2',
                print: 'AtkinsonHyperlegible-BoldItalic.ttf',
                weight: 700,
                italic: true,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'Braille Institute of America',
            sourceUrl: 'https://github.com/google/fonts/tree/main/ofl/atkinsonhyperlegible',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'available',
    },
    abeezee: {
        name: 'ABeeZee',
        purpose: 'druckschrift',
        cssFamily: 'ABeeZee',
        files: [
            {
                style: 'regular',
                woff2: 'ABeeZee-Regular.woff2',
                print: 'ABeeZee-Regular.ttf',
                weight: 400,
            },
            {
                style: 'italic',
                woff2: 'ABeeZee-Italic.woff2',
                print: 'ABeeZee-Italic.ttf',
                weight: 400,
                italic: true,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'Anja Meiners',
            sourceUrl: 'https://github.com/google/fonts/tree/main/ofl/abeezee',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'available',
    },
    opendyslexic: {
        name: 'OpenDyslexic',
        purpose: 'barrierefrei',
        cssFamily: 'OpenDyslexic',
        files: [
            {
                style: 'regular',
                woff2: 'OpenDyslexic-Regular.woff2',
                print: 'OpenDyslexic-Regular.otf',
                weight: 400,
            },
            {
                style: 'bold',
                woff2: 'OpenDyslexic-Bold.woff2',
                print: 'OpenDyslexic-Bold.otf',
                weight: 700,
            },
            {
                style: 'italic',
                woff2: 'OpenDyslexic-Italic.woff2',
                print: 'OpenDyslexic-Italic.otf',
                weight: 400,
                italic: true,
            },
            {
                style: 'boldItalic',
                woff2: 'OpenDyslexic-BoldItalic.woff2',
                print: 'OpenDyslexic-BoldItalic.otf',
                weight: 700,
                italic: true,
            },
        ],
        license: {
            spdx: 'OFL-1.1',
            author: 'Abbie Gonzalez',
            sourceUrl: 'https://github.com/antijingoist/opendyslexic',
            file: 'OFL.txt',
            attributionRequired: false,
        },
        status: 'available',
    },
};
/** Schriften, die in der Oberfläche angeboten werden (Dateien liegen vor). */
export function availableFonts() {
    return FONT_IDS.filter((id) => FONTS[id].status === 'available').map((id) => ({
        id,
        entry: FONTS[id],
    }));
}
/** Schriften mit Pflicht zur Namensnennung – für das PDF-Impressum. */
export function attributionTexts(ids) {
    const seen = new Set();
    for (const id of ids) {
        const { license } = FONTS[id];
        if (license.attributionRequired) {
            seen.add(license.attributionText ?? `${FONTS[id].name} (${license.author}, ${license.spdx})`);
        }
    }
    return [...seen].sort();
}
//# sourceMappingURL=fonts.js.map